// Expected behavior: show eight dated weeks with outlined purple circles, averages, and weekly changes.
// Gotchas: parse date-only week boundaries as UTC so browser time zones do not shift the chart by a day.

import { Box, Card, Text } from "@radix-ui/themes"

interface WeightMeasurement {
  id: string
  recorded_at: string
  weight_kg: number
}

interface WeeklyWeightAverage {
  start_date: string
  end_date: string
  weight_kg: number
  change_kg: number | null
}

export interface WeightHistory {
  start_date: string
  end_date: string
  measurements: WeightMeasurement[]
  weekly_averages: WeeklyWeightAverage[]
}

const chartWidth = 1_000
const chartHeight = 300
const plotLeft = 64
const plotRight = 24
const plotTop = 18
const plotBottom = 52
const millisecondsPerWeek = 7 * 24 * 60 * 60 * 1_000
const yTickIntervals = 4

function parseDate(dateString: string) {
  return new Date(`${dateString}T00:00:00Z`)
}

function formatWeekLabel(date: Date) {
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  })
}

function formatMeasurement(measurement: WeightMeasurement) {
  const recordedAt = new Date(measurement.recorded_at).toLocaleString()
  return `${recordedAt}: ${measurement.weight_kg.toFixed(1)} kg`
}

function formatWeightChange(changeKg: number) {
  const roundedChangeKg = Math.round(changeKg * 10) / 10
  return `${roundedChangeKg > 0 ? "+" : ""}${roundedChangeKg.toFixed(1)} kg`
}

export default function WeightHistoryChart({ history }: { history: WeightHistory }) {
  const weightValues = [
    ...history.measurements.map((measurement) => measurement.weight_kg),
    ...history.weekly_averages.map((average) => average.weight_kg),
  ]

  if (weightValues.length === 0) {
    return (
      <Card size="2">
        <Box p="5">
          <Text color="gray" size="2">
            No weight measurements were found in these eight weeks.
          </Text>
        </Box>
      </Card>
    )
  }

  const startTimestamp = parseDate(history.start_date).getTime()
  const endTimestamp = parseDate(history.end_date).getTime()
  const plotWidth = chartWidth - plotLeft - plotRight
  const plotHeight = chartHeight - plotTop - plotBottom
  const minimumValue = Math.min(...weightValues)
  const maximumValue = Math.max(...weightValues)
  const weightPadding = Math.max((maximumValue - minimumValue) * 0.15, 0.5)
  const minimumWeight = minimumValue - weightPadding
  const maximumWeight = maximumValue + weightPadding
  const weightRange = maximumWeight - minimumWeight
  const numberOfWeeks = Math.round((endTimestamp - startTimestamp) / millisecondsPerWeek)
  const weekStartDates = Array.from(
    { length: numberOfWeeks },
    (_, index) => new Date(startTimestamp + index * millisecondsPerWeek),
  )
  const yTickValues = Array.from(
    { length: yTickIntervals + 1 },
    (_, index) => minimumWeight + (weightRange * index) / yTickIntervals,
  )

  function horizontalPosition(date: Date) {
    const progress = (date.getTime() - startTimestamp) / (endTimestamp - startTimestamp)
    return plotLeft + progress * plotWidth
  }

  function verticalPosition(weightKg: number) {
    const progress = (weightKg - minimumWeight) / weightRange
    return plotTop + (1 - progress) * plotHeight
  }

  return (
    <Card size="2">
      <Box className="overflow-x-auto">
        <svg
          aria-label="Weight measurements and weekly averages for eight weeks"
          className="block h-auto min-w-[700px] w-full"
          role="img"
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        >
          {yTickValues.map((weightKg) => {
            const vertical = verticalPosition(weightKg)

            return (
              <g key={weightKg}>
                <line
                  stroke="var(--gray-a5)"
                  vectorEffect="non-scaling-stroke"
                  x1={plotLeft}
                  x2={chartWidth - plotRight}
                  y1={vertical}
                  y2={vertical}
                />
                <text
                  dominantBaseline="middle"
                  fill="var(--gray-11)"
                  fontSize="10"
                  textAnchor="end"
                  x={plotLeft - 10}
                  y={vertical}
                >
                  {weightKg.toFixed(1)}
                </text>
              </g>
            )
          })}

          {weekStartDates.map((weekStartDate, index) => {
            const horizontal = horizontalPosition(weekStartDate)

            return (
              <g key={weekStartDate.toISOString()}>
                <line
                  stroke="var(--gray-a6)"
                  vectorEffect="non-scaling-stroke"
                  x1={horizontal}
                  x2={horizontal}
                  y1={plotTop}
                  y2={chartHeight - plotBottom}
                />
                <text
                  fill="var(--gray-11)"
                  fontSize="10"
                  textAnchor={index === 0 ? "start" : "middle"}
                  x={horizontal}
                  y={chartHeight - 22}
                >
                  {formatWeekLabel(weekStartDate)}
                </text>
              </g>
            )
          })}

          {history.weekly_averages.map((average) => {
            const startHorizontal = horizontalPosition(parseDate(average.start_date))
            const endHorizontal = horizontalPosition(parseDate(average.end_date))
            const vertical = verticalPosition(average.weight_kg)

            return (
              <g key={average.start_date}>
                <line
                  stroke="var(--violet-9)"
                  strokeLinecap="round"
                  strokeWidth="3"
                  vectorEffect="non-scaling-stroke"
                  x1={startHorizontal}
                  x2={endHorizontal}
                  y1={vertical}
                  y2={vertical}
                >
                  <title>{`Weekly average: ${average.weight_kg.toFixed(1)} kg`}</title>
                </line>
                {average.change_kg !== null && (
                  <text
                    fill="var(--violet-9)"
                    fontSize="9"
                    fontWeight="500"
                    textAnchor="middle"
                    x={(startHorizontal + endHorizontal) / 2}
                    y={vertical + 14}
                  >
                    {formatWeightChange(average.change_kg)}
                  </text>
                )}
              </g>
            )
          })}

          {history.measurements.map((measurement) => (
            <circle
              key={measurement.id}
              cx={horizontalPosition(new Date(measurement.recorded_at))}
              cy={verticalPosition(measurement.weight_kg)}
              fill="none"
              r="3.5"
              stroke="var(--violet-9)"
              strokeOpacity="0.6"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            >
              <title>{formatMeasurement(measurement)}</title>
            </circle>
          ))}
        </svg>
      </Box>
    </Card>
  )
}
